import {
  afterRender,
  afterNextRender,
  booleanAttribute,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  numberAttribute,
  signal,
} from '@angular/core';

/**
 * Automatically adjusts `<textarea>` height to fit its content.
 *
 * Measures height via `scrollHeight` on the actual element. Supports
 * configurable min/max row limits and responds to container width changes.
 *
 * @example
 * ```html
 * <textarea appTextareaAutosize appTextareaAutosizeMinRows="3" appTextareaAutosizeMaxRows="7"></textarea>
 * ```
 */
@Directive({
  selector: 'textarea[appTextareaAutosize]',
  host: { '(input)': 'resize()' },
})
export class TextareaAutosizeDirective {
  private readonly element = inject<ElementRef<HTMLTextAreaElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Resize observer instance.
   */
  private resizeObserver: ResizeObserver | null = null;

  /**
   * Last known textarea value, used to detect programmatic changes.
   */
  private readonly lastValue = signal<string | null>(null);

  /**
   * Last known minRows value, used to detect dynamic input changes.
   */
  private readonly lastMinRows = signal<number | null>(null);

  /**
   * Last known maxRows value, used to detect dynamic input changes.
   */
  private readonly lastMaxRows = signal<number | null>(null);

  /**
   * Last known unlimited value, used to detect dynamic input changes.
   */
  private readonly lastUnlimited = signal<boolean | null>(null);

  /**
   * Minimum number of visible rows.
   */
  public readonly minRows = input(3, {
    transform: numberAttribute,
    alias: 'appTextareaAutosizeMinRows',
  });

  /**
   * Maximum number of visible rows (0 = unlimited).
   */
  public readonly maxRows = input(25, {
    transform: numberAttribute,
    alias: 'appTextareaAutosizeMaxRows',
  });

  /**
   * When true, ignores `maxRows` and allows the textarea to grow without limit.
   */
  public readonly unlimited = input(false, {
    transform: booleanAttribute,
    alias: 'appTextareaAutosizeUnlimited',
  });

  constructor() {
    // One-time setup for container width observation.
    afterNextRender(() => this.observeContainerWidth());

    // Runs after every render cycle to detect programmatic value/input changes.
    afterRender(() => {
      const value = this.element.nativeElement.value;
      const minRows = this.minRows();
      const maxRows = this.maxRows();
      const unlimited = this.unlimited();

      if (
        value !== this.lastValue() ||
        minRows !== this.lastMinRows() ||
        maxRows !== this.lastMaxRows() ||
        unlimited !== this.lastUnlimited()
      ) {
        this.lastValue.set(value);
        this.lastMinRows.set(minRows);
        this.lastMaxRows.set(maxRows);
        this.lastUnlimited.set(unlimited);

        this.resize();
      }
    });
  }

  /**
   * Recalculates and applies the textarea height.
   */
  public resize(): void {
    const textarea = this.element.nativeElement;

    // Sync lastValue so afterRender skips redundant resize after user input.
    this.lastValue.set(textarea.value);

    const computed = getComputedStyle(textarea);

    const lineHeight = parseFloat(computed.lineHeight) || 20;
    const paddingY = parseFloat(computed.paddingTop) + parseFloat(computed.paddingBottom);
    const borderY = parseFloat(computed.borderTopWidth) + parseFloat(computed.borderBottomWidth);

    const minRows = this.minRows();
    const maxRows = this.maxRows();
    const unlimited = this.unlimited();
    const minHeight = minRows * lineHeight + paddingY + borderY;
    const maxHeight = !unlimited && maxRows > 0 ? maxRows * lineHeight + paddingY + borderY : 0;

    // Collapse -> measure -> set (synchronous, no visual flash)
    textarea.style.overflow = 'hidden';
    textarea.style.height = '0px';

    const scrollHeight = textarea.scrollHeight; // content + padding
    const targetHeight = scrollHeight + borderY; // border-box total

    const clampedHeight =
      maxHeight > 0
        ? Math.min(Math.max(targetHeight, minHeight), maxHeight)
        : Math.max(targetHeight, minHeight);

    textarea.style.height = `${clampedHeight}px`;
    textarea.style.overflow = maxHeight > 0 && targetHeight > maxHeight ? 'auto' : 'hidden';
  }

  /**
   * Watches parent element width to re-measure when the container resizes.
   */
  private observeContainerWidth(): void {
    const parent = this.element.nativeElement.parentElement;
    if (!parent) return;

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(parent);

    this.destroyRef.onDestroy(() => this.resizeObserver?.disconnect());
  }
}
